
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref, toRefs, watch } from 'vue';
import {
  AemAsset,
  HorizontalAlignment,
  ImageSources,
  PriceTrackingEventType,
  PromotionTrackingEvent,
  StageCampaignData,
  StageTheme,
  VerticalAlignment,
} from './types/stage-campaign-item.types';
import { sanitizeHTML } from './utils/HTMLSanitizer';
import {
  EmhConfig,
  Product,
  SearchApiMode,
  SearchResultsApi,
  TrackingApplication,
} from '@dh-io-emh/search-result-store';
import { CONTEXT_TYPES } from '@dh-io-cscs/user-context-manager/dist/common/constants';
import { ContextType, UCMC_CONNECTION_NAME, UserContextManagerState } from '@dh-io-cscs/user-context-manager/dist';
import { getTrackingDispatchers, TrackingEvent } from '@owc/connection-aem-tracking';
import { getAemEnvironmentVariables } from '@dh-io-emh/config-store';
import { initializeStore, SeamlessCoreStore } from '@seamless/store';
import { AemStage, aemStageToPlsEnvironment, initEmhContext } from './utils/emhContext';
import { TFunction } from 'i18next';
import { FootnoteReferenceGroup, PriceDisplayEventData, SupportedMarket } from '@dh-io-pls/price-display/dist/types';
import { usePriceDisplayFootnotes } from './utils/footnotes';
import CampaignImage from './components/CampaignImage.vue';
import CampaignTag from './components/CampaignTag.vue';
import MonthlyPriceInformation from './components/MonthlyPriceInformation.vue';
import PurchasePriceInformation from './components/PurchasePriceInformation.vue';
import HideNotification from './components/HideNotification.vue';
import { useAddSrpFragmentToLink } from './composables/srpLink';
import PrimaryButton from './components/PrimaryButton.vue';
import SecondaryButton from './components/SecondaryButton.vue';
import { getWebcomponentRootFromElement } from './utils/webcomponent';
import { isTrue } from './utils/AEM';
import { connectionName, RegionPickerConnectionState } from '@dh-io-emh/region-picker-store';
import InlineSvg from 'vue-inline-svg';

const defaultImageData: AemAsset = {
  source: '',
  description: '',
  title: '',
};

const imageBreakpoints = [
  { mq: 'mq6', width: 1024 },
  { mq: 'mq4', width: 768 },
  { mq: 'mq2', width: 320 },
];

const hostClasses = ['emh-stage-campaign-item', 'wb-slider__item', 'wb-new-colors'];

export default defineComponent({
  name: 'EmhStageCampaignItem',
  components: {
    CampaignTag,
    CampaignImage,
    InlineSvg,
    MonthlyPriceInformation,
    PurchasePriceInformation,
    PrimaryButton,
    SecondaryButton,
    HideNotification,
  },
  props: {
    datajson: {
      type: String,
      default: '',
    },
    index: {
      type: Number,
      default: 0,
    },
  },
  emits: ['popups-open', 'popups-close', 'theme-active', 'theme-change'],
  setup(props, { emit }) {
    const { datajson, index } = toRefs(props);

    const initialized = ref(false);

    const language = ref('');
    const country = ref('');
    const aemStage = ref<AemStage>('dev');

    let store: SeamlessCoreStore;
    let searchApi: SearchResultsApi;
    let unregisterFootnotes = ref<() => Promise<void>>();

    const hide = ref('false');
    const theme = ref(StageTheme.light);
    const horizontalAlign = ref(HorizontalAlignment.left);
    const verticalAlign = ref(VerticalAlignment.middle);

    const imageUrl = ref(defaultImageData);
    const imageUrlMid = ref(defaultImageData);
    const imageUrlSmall = ref(defaultImageData);
    const showCampaignTag = ref('false');
    const campaignTagLabel = ref<string | undefined>('');
    const analytics = ref('');
    const headline = ref('');
    const description = ref('');
    const images = ref<string[]>([]);
    const footnoteReferences = ref<FootnoteReferenceGroup[]>();
    const postCode = ref<string | null>(null);

    const ctaLink = ref<string | undefined>('');
    const ctaLabel = ref<string | undefined>('');
    const secondaryLink = ref<string | undefined>('');
    const secondaryLabel = ref<string | undefined>('');

    const fcisTrackingEventSent = ref(false);
    const hasFcisError = ref(false);
    const fcisReady = ref(false);
    const ppTrackingEventSent = ref(false);

    const delta = ref(10);
    const isMouseMoved = ref(false);
    const isMouseDown = ref(true);
    const initialClientX = ref(0);
    const initialClientY = ref(0);

    const t = ref<TFunction>();

    const srpLink = ref<string | undefined>('');
    const pictureSources = ref<ImageSources[]>();
    const pictureFallbackImgSrc = ref('');
    const pictureDescription = ref('');
    const product = ref<Product>();
    const priceEventData = ref<PriceDisplayEventData>();
    const isFcisPopupOpen = ref(false);
    const isPpBreakdownOpen = ref(false);
    const userContext = ref<ContextType>(CONTEXT_TYPES.B2C);
    const isAuthorMode = ref(false);
    const emhConfig = ref<EmhConfig>();
    const hostElement = ref<HTMLElement>();

    const { addSrpFragmentToLink, isSrpLink } = useAddSrpFragmentToLink();

    onMounted(async () => {
      const { runMode } = await getAemEnvironmentVariables();

      isAuthorMode.value = runMode?.toLowerCase() === 'author';

      onDataJsonPropChange(datajson.value);

      lazyLoad().then(async () => {
        const emhContext = await initEmhContext();

        language.value = emhContext.language;
        country.value = emhContext.country;
        aemStage.value = emhContext.aemStage;
        searchApi = emhContext.searchApi;
        t.value = emhContext.t;
        emhConfig.value = emhContext.emhConfig;

        if (srpLink.value) {
          subscribeUserContextChanges();
          subscribePostCodeChanges();
          fetchProducts();
        }
      });
    });

    onBeforeUnmount(async () => {
      if (unregisterFootnotes.value) {
        await unregisterFootnotes.value();
      }
    });

    const applyClassesToHost = () => {
      if (!initialized.value) {
        return;
      }

      const host = getWebcomponentRootFromElement(hostElement.value);

      if (!host) {
        return;
      }

      hostClasses.forEach((cl) => {
        if (!host.classList.contains(cl)) {
          host.classList.add(cl);
        }
      });
    };

    const subscribePostCodeChanges = () => {
      const store = initializeStore();

      store.subscribe(connectionName, (regionPickerState: RegionPickerConnectionState | null) => {
        if (regionPickerState && regionPickerState.region) {
          postCode.value = regionPickerState.region;
        }
      });
    };

    const subscribeUserContextChanges = () => {
      store = initializeStore();

      store.subscribe(UCMC_CONNECTION_NAME, (contextState: UserContextManagerState | null) => {
        if (contextState) {
          userContext.value = contextState.context;
        }
      });
    };

    const onDataJsonPropChange = (value: string) => {
      if (!value) {
        return;
      }

      try {
        const { payload } = JSON.parse(value) as { payload: StageCampaignData };

        if (!payload) {
          return;
        }

        imageUrl.value = payload.imageUrl;
        imageUrlSmall.value = payload.imageUrlSmall;
        imageUrlMid.value = payload.imageUrlMid;

        ctaLink.value = payload.ctaLink;
        secondaryLink.value = payload.secondaryLink;
        headline.value = sanitizeHTML(payload.headline);
        hide.value = payload.hide;
        description.value = sanitizeHTML(payload.description);
        theme.value = payload.theme;
        srpLink.value = getSrpLink(payload.ctaLink, payload.secondaryLink);
        showCampaignTag.value = payload.showCampaignTag;
        images.value = [imageUrl.value.source, imageUrlMid.value.source, imageUrlSmall.value.source]
          .filter(Boolean)
          .map((imageSrc, i) => imageSrc.replace('{mq}', imageBreakpoints[i].mq));

        campaignTagLabel.value = payload.campaignTagLabel;
        secondaryLabel.value = payload.secondaryLabel;
        ctaLabel.value = payload.ctaLabel;

        horizontalAlign.value = payload.horizontalAlign;
        verticalAlign.value = payload.verticalAlign;

        if (images.value.length !== 3) {
          console.error(`emh-stage-campaign-item: Missing image sources, need 3 images but got ${images.value.length}`);
        } else {
          // Use smallest image as fallback image
          pictureFallbackImgSrc.value = imageUrlSmall.value.source.replace('{mq}', 'mq2');
          pictureDescription.value = imageUrl.value.description;
          pictureSources.value = images.value.map((src, i) => ({
            src,
            media: `(min-width: ${imageBreakpoints[i].width}px)`,
          }));
        }

        applyClassesToHost();
        initialized.value = true;
      } catch (ex) {
        console.error('failed to parse datajson', ex);
      }
    };

    const fetchProducts = async () => {
      if (!srpLink.value) {
        console.error('srp link missing');

        return;
      }

      const srpUrl = new URL(srpLink.value);
      let sort = '';
      const filterParams: string[] = [];

      srpUrl.searchParams.forEach((value, key) => {
        const plainKey: string = key.replace(emhConfig.value?.aemApp?.namespace || 'emh', '');

        if (plainKey === 'sort') {
          sort = value;
        } else {
          // TODO: Whitelist of possible filter keys might be necessary
          // because the URL might contain non-filter related params, like tracking

          // Split value before adding it to the filterParams because of
          // different param handling in AEM/Seamless Router and our legacy SPA
          value.split(',').forEach((value) => filterParams.push(`${plainKey}:${value}`));
        }
      });

      const filterStr = filterParams.join(':');

      const result = await searchApi.filterProducts(filterStr, sort, SearchApiMode.FULL, false);

      product.value = result.products[0];
    };

    const handleInViewport = (): void => {
      emit('theme-change', theme.value);
    };

    const handleFcisError = () => {
      hasFcisError.value = true;
    };

    const handleFcisReady = () => {
      fcisReady.value = true;
    };

    const handlePopupToggle = (value: boolean) => {
      if (isFcisPopupOpen.value === value) return;

      if (value && !fcisTrackingEventSent.value) sendTrackingEvent(PriceTrackingEventType.fcis);

      isFcisPopupOpen.value = value;
      togglePopupState();
    };

    const lazyLoad = async (): Promise<void> => {
      await nextTick();

      const inViewportPromise = new Promise<void>((resolve) => {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                handleInViewport();
                resolve();
              }
            });
          },
          { threshold: 0.2 },
        );

        observer.observe(hostElement.value as Element);
      });

      return inViewportPromise;
    };

    const onPriceDisplayDataUpdate = async (event: CustomEvent<PriceDisplayEventData>): Promise<void> => {
      priceEventData.value = { ...event.detail };

      if (!unregisterFootnotes.value && priceEventData.value.footnotes) {
        const { cleanupFootnotes } = await usePriceDisplayFootnotes(priceEventData.value.footnotes, (references) => {
          footnoteReferences.value = references;
        });

        unregisterFootnotes.value = cleanupFootnotes;
      }
    };

    const scrollToFootnotes = () => {
      //Please use globalelem-footnote-component when moving to the vehicles repo
      //const footnoteContainer = document.getElementsByTagName('globalelem-footnote-component')[0];
      const footnoteContainer = document.getElementsByTagName('mbmxp-footnote-component')[0];

      if (footnoteContainer) {
        footnoteContainer.scrollIntoView({ behavior: 'smooth' });
      }
    };

    const track = (event: TrackingEvent): Promise<void> => {
      return getTrackingDispatchers().then((dispatchers) => {
        dispatchers.pushTrackingEvent(event);
      });
    };

    const getImageFilenameFromPath = (path: string): string => path.substring(path.lastIndexOf('/') + 1, path.length);

    const isPopupOpen = computed(() => isFcisPopupOpen.value || isPpBreakdownOpen.value);

    const getPromotionTrackingEvent = (): PromotionTrackingEvent => ({
      id: undefined,
      name: analytics.value ?? 'not_set',
      creative: images.value[0] ? getImageFilenameFromPath(images.value[0]) : 'not_set',
      position: `campaign_banner.${index.value}`,
    });

    const handleButtonClick = (event: MouseEvent) => {
      const target = event.target as HTMLAnchorElement;
      const link = target.href;

      if (link) {
        track({
          application: trackingApplication.value,
          event: 'click',
          eventDetail: {
            category: 'link',
            action: 'banner.campaign',
            label: `cta.${target.text}`,
            URL: link,
            promotion: [getPromotionTrackingEvent()],
          },
        });
      }
    };

    const locale = computed(() => {
      if (!language.value || !country.value) return;

      return `${language.value.toLowerCase()}-${country.value.toUpperCase()}` as SupportedMarket;
    });

    const plsEnvironment = computed(() => aemStageToPlsEnvironment(aemStage.value));
    const showHideNotificationComponent = computed(() => t.value && isTrue(hide.value) && isAuthorMode.value);

    const handleMouseDown = (event: MouseEvent): void => {
      isMouseMoved.value = false;
      isMouseDown.value = true;
      initialClientX.value = event.clientX;
      initialClientY.value = event.clientY;
    };

    const handleMouseMove = (event: MouseEvent): void => {
      if (!isMouseDown.value || isMouseMoved.value) return;

      const distanceX = Math.abs(initialClientX.value - event.clientX);
      const distanceY = Math.abs(initialClientY.value - event.clientY);

      if (distanceX >= delta.value || distanceY >= delta.value) {
        isMouseMoved.value = true;
      }
    };

    const handleMouseUp = (): void => {
      isMouseDown.value = false;
    };

    const onFcisPopupToggle = (value: boolean): void => {
      if (isFcisPopupOpen.value === value) return;

      if (value && !fcisTrackingEventSent.value) sendTrackingEvent(PriceTrackingEventType.fcis);

      isFcisPopupOpen.value = value;
      togglePopupState();
    };

    const onPpBreakdownToggle = (value: boolean): void => {
      if (isPpBreakdownOpen.value === value) return;

      if (value && !ppTrackingEventSent.value) sendTrackingEvent(PriceTrackingEventType.pp);

      isPpBreakdownOpen.value = value;
      togglePopupState();
    };

    const getSrpLink = (cta?: string, secondary?: string): string | undefined => {
      // Find an SRP link in one of our two CTAs
      let srpLink;

      if (cta && isSrpLink(cta)) {
        srpLink = cta;
      } else if (secondary && isSrpLink(secondary)) {
        srpLink = secondary;
      }

      if (!srpLink) {
        return;
      }

      // Add location.origin to the srpLink if it's relative
      if (srpLink && !srpLink.startsWith('http')) {
        srpLink = window.location.origin + srpLink;
      }

      return srpLink;
    };

    const trackingApplication = computed(
      (): TrackingApplication => ({
        id: 'emh',
        version: '',
        subId: emhConfig.value?.tracking.subId || '',
        type: emhConfig.value?.tracking.assortmentType || '',
        parentApplicationId: 'aem',
        businessUnit: userContext.value.toLowerCase() || 'b2c',
      }),
    );

    const sendTrackingEvent = (type: PriceTrackingEventType): void => {
      const trackingEvent: TrackingEvent = {
        event: 'click',
        application: trackingApplication.value,
        eventDetail: {
          category: 'feature',
          action: 'price_tooltip',
          label: type === PriceTrackingEventType.fcis ? 'monthly_rate' : 'total_price',
        },
      };

      track(trackingEvent)
        .then(() => {
          if (type === PriceTrackingEventType.fcis) {
            fcisTrackingEventSent.value = true;
          }

          if (type === PriceTrackingEventType.pp) {
            ppTrackingEventSent.value = true;
          }
        })
        .catch((error) => {
          console.error('failed to send tracking event', error);
        });
    };

    const togglePopupState = (): void => {
      isPopupOpen.value ? emit('popups-open') : emit('popups-close');
    };

    const renderStageCampaignItem = computed(() => initialized.value || (!isTrue(hide.value) && isAuthorMode.value));

    const showPriceInformation = computed(() =>
      Boolean(product.value && locale.value && srpLink.value && userContext.value),
    );

    const showMonthlyPriceInformation = computed(() => !hasFcisError.value && emhConfig.value && t.value);
    const showPurchasePriceInformation = computed(() => t.value);

    watch(datajson, () => onDataJsonPropChange(datajson.value), { deep: true });

    const elementClasses = computed(() => ({
      'emh-stage-campaign-item--dark': theme.value === StageTheme.dark,
      'emh-stage-campaign-item--light': theme.value === StageTheme.light,
      'emh-stage-campaign-item--horizontal-center': horizontalAlign.value === HorizontalAlignment.center,
      'emh-stage-campaign-item--horizontal-right': horizontalAlign.value === HorizontalAlignment.right,
      'emh-stage-campaign-item--vertical-middle': verticalAlign.value === VerticalAlignment.middle,
      'emh-stage-campaign-item--vertical-top': verticalAlign.value === VerticalAlignment.top,
    }));

    return {
      addSrpFragmentToLink,
      campaignTagLabel,
      ctaLabel,
      ctaLink,
      elementClasses,
      renderStageCampaignItem,
      headline,
      description,
      footnoteReferences,
      fcisReady,
      hostClasses,
      hostElement,
      handleButtonClick,
      handleMouseDown,
      handleMouseMove,
      handleMouseUp,
      handleFcisError,
      handleFcisReady,
      handlePopupToggle,
      isAuthorMode,
      onFcisPopupToggle,
      onPriceDisplayDataUpdate,
      onPpBreakdownToggle,
      pictureDescription,
      pictureSources,
      pictureFallbackImgSrc,
      plsEnvironment,
      priceEventData,
      product,
      postCode,
      secondaryLabel,
      showHideNotificationComponent,
      showMonthlyPriceInformation,
      showPurchasePriceInformation,
      showCampaignTag,
      secondaryLink,
      srpLink,
      language,
      locale,
      country,
      emhConfig,
      userContext,
      showPriceInformation,
      isTrue,
      scrollToFootnotes,
      t,
    };
  },
});
